import React, { useState, useEffect } from 'react';
import { QuizQuestion } from '../types';

export interface ChapterQuizProps {
  questions: QuizQuestion[];
  onQuizComplete: (score: number, totalQuestions: number) => void;
  onBackToContent?: () => void;
  className?: string;
  nextPageTitle?: string;
  onContinueNow?: () => void;
  onQuizRestart?: () => void;
}

interface QuizState {
  currentQuestionIndex: number;
  selectedAnswers: (number | null)[];
  showFeedback: boolean;
  isComplete: boolean;
  score: number;
}

const ChapterQuiz: React.FC<ChapterQuizProps> = ({
  questions,
  onQuizComplete,
  onBackToContent,
  className = '',
  nextPageTitle,
  onContinueNow,
  onQuizRestart
}) => {
  const [quizState, setQuizState] = useState<QuizState & { hasSubmitted: boolean }>({
    currentQuestionIndex: 0,
    selectedAnswers: new Array(questions.length).fill(null),
    showFeedback: false,
    isComplete: false,
    score: 0,
    hasSubmitted: false
  });

  const currentQuestion = questions[quizState.currentQuestionIndex];
  const isLastQuestion = quizState.currentQuestionIndex === questions.length - 1;
  const hasSelectedAnswer = quizState.selectedAnswers[quizState.currentQuestionIndex] !== null;

  // Calculate score when quiz is completed
  useEffect(() => {
    if (quizState.isComplete && !quizState.hasSubmitted) {
      const correctAnswers = quizState.selectedAnswers.reduce((count: number, answer, index) => {
        return answer === questions[index].correctAnswer ? count + 1 : count;
      }, 0);

      const finalScore = correctAnswers;
      setQuizState(prev => ({ ...prev, score: finalScore, hasSubmitted: true }));
      onQuizComplete(finalScore, questions.length);
    }
  }, [quizState.isComplete, quizState.hasSubmitted, quizState.selectedAnswers, questions, onQuizComplete]);

  const handleAnswerSelect = (answerIndex: number) => {
    if (quizState.showFeedback) return; // Prevent changing answer after feedback is shown

    const newSelectedAnswers = [...quizState.selectedAnswers];
    newSelectedAnswers[quizState.currentQuestionIndex] = answerIndex;

    setQuizState(prev => ({
      ...prev,
      selectedAnswers: newSelectedAnswers
    }));
  };

  // Handle keyboard navigation for answer options
  const handleAnswerKeyDown = (event: React.KeyboardEvent, answerIndex: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAnswerSelect(answerIndex);
    }
  };

  const handleShowFeedback = () => {
    if (!hasSelectedAnswer) return;

    setQuizState(prev => ({
      ...prev,
      showFeedback: true
    }));
  };

  const handleNextQuestion = () => {
    if (isLastQuestion) {
      // Complete the quiz
      setQuizState(prev => ({
        ...prev,
        isComplete: true
      }));
    } else {
      // Move to next question
      setQuizState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        showFeedback: false
      }));
    }
  };

  const handleRestartQuiz = () => {
    if (onQuizRestart) {
      onQuizRestart();
    }

    setQuizState({
      currentQuestionIndex: 0,
      selectedAnswers: new Array(questions.length).fill(null),
      showFeedback: false,
      isComplete: false,
      score: 0,
      hasSubmitted: false
    });
  };

  const getScoreColor = (score: number, total: number) => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return 'text-green-600 dark:text-green-400';
    if (percentage >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreMessage = (score: number, total: number) => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return 'Výborně! Máte dobré znalosti tématu.';
    if (percentage >= 60) return 'Dobře, ale je prostor pro zlepšení.';
    return 'Doporučujeme si téma znovu prostudovat.';
  };



  if (quizState.isComplete) {
    return (
      <div className={`max-w-xl mx-auto ${className} flex items-center justify-center min-h-[50vh]`} role="main" aria-labelledby="quiz-results-heading">
        <div className="w-full bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700/50 p-8 sm:p-10 shadow-xl text-center">

          {/* Header Icon */}
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white shadow-lg shadow-green-500/30 animate-bounce-subtle">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          <h2 id="quiz-results-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Kvíz dokončen!
          </h2>

          {/* Score Display */}
          <div className="mb-8">
            <div className="text-6xl sm:text-7xl font-black bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-2 tracking-tight">
              {Math.round((quizState.score / questions.length) * 100)}%
            </div>
            <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
              {quizState.score} z {questions.length} správně
            </p>
            <p className={`mt-2 text-sm font-semibold tracking-wide uppercase ${getScoreColor(quizState.score, questions.length)}`}>
              {getScoreMessage(quizState.score, questions.length)}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {/* Primary Action: Next Page (if available) - Prominent 3D Button */}
            {nextPageTitle && onContinueNow ? (
              <button
                onClick={onContinueNow}
                className="w-full py-4 px-6 bg-brand-orange text-white text-lg font-bold rounded-xl 
                         border-2 border-b-[6px] border-orange-600
                         hover:bg-orange-600
                         active:border-b-2 active:translate-y-[4px]
                         focus:outline-none focus:ring-2 focus:ring-orange-500/50 
                         flex items-center justify-center gap-3 group
                         transition-all duration-200"
              >
                <span>Další: {nextPageTitle}</span>
                <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            ) : null}

            {/* Secondary Actions Grid */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleRestartQuiz}
                className="py-3.5 px-4 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold rounded-xl
                         border-2 border-b-[6px] border-gray-300 dark:border-gray-600
                         hover:border-gray-400 dark:hover:border-gray-500
                         active:border-b-2 active:translate-y-[4px]
                         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500/50 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Zkusit znovu
              </button>

              {onBackToContent && (
                <button
                  onClick={onBackToContent}
                  className="py-3.5 px-4 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold rounded-xl
                           border-2 border-b-[6px] border-gray-300 dark:border-gray-600
                           hover:border-gray-400 dark:hover:border-gray-500
                           active:border-b-2 active:translate-y-[4px]
                           transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500/50 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m7 7l-7-7 7-7" />
                  </svg>
                  Zpět k textu
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-3xl mx-auto ${className}`} role="main" aria-labelledby="quiz-heading">
      {/* Quiz Header */}
      <header className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h2 id="quiz-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Kvíz
          </h2>
          <div className="text-sm sm:text-base text-gray-500 dark:text-gray-400" aria-live="polite">
            Otázka {quizState.currentQuestionIndex + 1} z {questions.length}
          </div>
        </div>

        {/* Progress Bar */}
        <div
          className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2"
          role="progressbar"
          aria-valuenow={quizState.currentQuestionIndex + 1}
          aria-valuemin={1}
          aria-valuemax={questions.length}
          aria-label={`Pokrok kvízu: otázka ${quizState.currentQuestionIndex + 1} z ${questions.length}`}
        >
          <div
            className="bg-gradient-to-r from-orange-400 to-orange-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${((quizState.currentQuestionIndex + 1) / questions.length) * 100}%`
            }}
            aria-hidden="true"
          />
        </div>
      </header>

      {/* Question Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-8 mb-4 shadow-lg">
        {/* Question */}
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-8 leading-relaxed">
          {currentQuestion.question}
        </h3>

        {/* Answer Options */}
        <fieldset className="space-y-3 mb-4 sm:mb-8">
          <legend className="sr-only">Vyberte odpověď</legend>
          {currentQuestion.options.map((option, index) => {
            const isSelected = quizState.selectedAnswers[quizState.currentQuestionIndex] === index;
            const isCorrect = index === currentQuestion.correctAnswer;
            const showCorrectness = quizState.showFeedback;

            let buttonClasses = 'w-full px-6 py-3.5 text-left rounded-xl border-2 border-b-[6px] transition-all duration-200 focus:outline-none focus:ring-2 font-bold text-base ';

            if (showCorrectness) {
              if (isCorrect) {
                // Correct answer - green with pressed state
                buttonClasses += 'border-green-500 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 focus:ring-green-500 translate-y-[2px] shadow-sm';
              } else if (isSelected && !isCorrect) {
                // Wrong answer - red with pressed state
                buttonClasses += 'border-red-500 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 focus:ring-red-500 translate-y-[2px] shadow-sm';
              } else {
                // Other options when feedback shown - muted
                buttonClasses += 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 focus:ring-gray-500';
              }
            } else if (isSelected) {
              // Selected but not checked yet - orange with pressed state (like wizard)
              buttonClasses += 'border-brand-orange bg-[#fed7aa] text-brand-orange focus:ring-orange-500 translate-y-[2px] shadow-sm';
            } else {
              // Unselected - default with hover and active states
              buttonClasses += 'border-gray-300 dark:border-gray-600 hover:border-brand-orange/50 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-orange-500/50 active:border-b-2 active:translate-y-[6px]';
            }

            const ariaLabel = `Možnost ${String.fromCharCode(65 + index)}: ${option}${isSelected ? ' - vybrána' : ''}${showCorrectness && isCorrect ? ' - správná odpověď' : ''}${showCorrectness && isSelected && !isCorrect ? ' - nesprávná odpověď' : ''}`;

            return (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                onKeyDown={(e) => handleAnswerKeyDown(e, index)}
                disabled={quizState.showFeedback}
                className={buttonClasses}
                data-testid={`option-${index}`}
                aria-label={ariaLabel}
                role="radio"
                aria-checked={isSelected}
              >
                <div className="flex items-center gap-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${isSelected && !showCorrectness ? 'border-brand-orange bg-brand-orange text-white' :
                    showCorrectness && isCorrect ? 'border-green-500 bg-green-500 text-white' :
                      showCorrectness && isSelected && !isCorrect ? 'border-red-500 bg-red-500 text-white' :
                        'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                    }`}>
                    <span className="text-sm font-semibold">
                      {String.fromCharCode(65 + index)}
                    </span>
                  </div>
                  <span className="flex-1">{option}</span>

                  {/* Correct/Incorrect Icons */}
                  {showCorrectness && isCorrect && (
                    <div className="text-green-600 dark:text-green-400 flex-shrink-0">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}

                  {showCorrectness && isSelected && !isCorrect && (
                    <div className="text-red-600 dark:text-red-400 flex-shrink-0">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </fieldset>

        {/* Explanation */}
        {quizState.showFeedback && currentQuestion.explanation && (
          <div className="mb-4 sm:mb-8 p-4 sm:p-6 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-xl" role="region" aria-labelledby="explanation-heading">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" aria-hidden="true">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 id="explanation-heading" className="font-semibold text-blue-700 dark:text-blue-400 mb-1">Vysvětlení</h4>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed">{currentQuestion.explanation}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          {onBackToContent && quizState.currentQuestionIndex === 0 && !quizState.showFeedback && (
            <button
              onClick={onBackToContent}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 
                       bg-white dark:bg-gray-800 rounded-xl border-2 border-b-[6px] border-gray-300 dark:border-gray-600
                       hover:border-gray-400 dark:hover:border-gray-500
                       active:border-b-2 active:translate-y-[4px]
                       focus:outline-none focus:ring-2 focus:ring-gray-500/50
                       transition-all duration-200 font-bold"
              aria-label="Vrátit se k obsahu kapitoly"
            >
              ← Zpět k obsahu
            </button>
          )}

          <div className="sm:ml-auto">
            {!quizState.showFeedback ? (
              <button
                onClick={handleShowFeedback}
                disabled={!hasSelectedAnswer}
                className="w-full sm:w-auto px-8 py-3 bg-brand-orange text-white rounded-xl border-2 border-b-[6px] border-orange-600
                         hover:bg-orange-600
                         active:border-b-2 active:translate-y-[4px]
                         focus:outline-none focus:ring-2 focus:ring-orange-500/50
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-y-0 disabled:active:border-b-[6px]
                         transition-all duration-200 font-bold text-base"
                data-testid="check-answer-button"
                aria-label={hasSelectedAnswer ? "Zkontrolovat vybranou odpověď" : "Nejprve vyberte odpověď"}
              >
                Zkontrolovat odpověď
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="w-full sm:w-auto px-8 py-3 bg-brand-orange text-white rounded-xl border-2 border-b-[6px] border-orange-600
                         hover:bg-orange-600
                         active:border-b-2 active:translate-y-[4px]
                         focus:outline-none focus:ring-2 focus:ring-orange-500/50
                         transition-all duration-200 font-bold text-base"
                data-testid="next-question-button"
                aria-label={isLastQuestion ? "Dokončit kvíz a zobrazit výsledky" : "Přejít na další otázku"}
              >
                {isLastQuestion ? 'Dokončit kvíz' : 'Další otázka'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterQuiz;